'use client';

import clsx from 'clsx';
import React, { type ComponentPropsWithRef, type ElementType, type ForwardedRef, forwardRef } from 'react';
import { BASE_PATH } from '@/constants/constants';
import { Loader } from '@/components/Loader/Loader';
import './Button.scss';

export type Variants = 'primary' | 'secondary' | 'tertiary' | 'navbar';
type ButtonTypes = 'submit' | 'reset' | 'search' | 'button';
type AsButtonElements = 'button';
type AsLinkElements<T> = T;
export type BaseButtonProps = {
	children?: React.ReactNode;
	className?: string;
	ariaLabel?: string;
	disabled?: boolean;
	inline?: boolean;
	onClick?: (e: React.SyntheticEvent) => void;
	icon?: string;
	iconSize?: string;
	iconPosition?: 'right' | 'left' | 'none';
	[x: string]: any;
};

type VariantProps =
	| { variant?: 'primary' | 'secondary' | 'navbar'; variantSmallScreen?: never; theme?: never; underline?: never }
	| { variant?: 'tertiary'; variantSmallScreen?: 'primary' | 'secondary'; theme?: 'light' | 'dark'; underline?: boolean };

type BaseProps = BaseButtonProps & VariantProps;

export type AsButtonProps = BaseProps & { as?: AsButtonElements; type?: ButtonTypes; loading?: boolean };

export type AsLinkProps<T> = BaseProps & { as?: AsLinkElements<T>; href?: string; target?: string };

export type ButtonProps<T> = AsButtonProps | AsLinkProps<T>;

// We will need to pass ref to the button element in order e.g to be able to focus it programmatically (for example when modal closes we want to bring the focus back to the button for a11y).
// forwardRef and Typescript don't work well with generics. This is a workaround to fix it.
type FixedForwardRef = <T, P = {}>(
	render: (props: P, ref: React.Ref<T>) => React.ReactNode
) => (props: P & React.RefAttributes<T>) => React.ReactNode;
const fixedForwardRef = forwardRef as FixedForwardRef;
type DistributiveOmit<T, TOmitted extends PropertyKey> = T extends any ? Omit<T, TOmitted> : never;

const ButtonBase = <TAs extends ElementType>(
	{
		children,
		as = 'button',
		variant,
		className,
		ariaLabel,
		disabled,
		inline,
		onClick,
		icon,
		iconSize = '1em',
		iconPosition,
		loading,
		theme,
		underline,
		variantSmallScreen,
		type = 'button',
		...rest
	}: ButtonProps<TAs> & DistributiveOmit<ComponentPropsWithRef<ElementType extends TAs ? 'button' : TAs>, 'as'>,
	ref: ForwardedRef<HTMLAnchorElement | HTMLButtonElement>
) => {
	const hasValidIcon = icon && icon !== 'none' && iconPosition && iconPosition !== 'none';
	const buttonClasses = clsx('button', className, { 'd-inline': inline });
	const { ...safeRest } = rest;
	const buttonAttributes = {
		onClick,
		...(as === 'button' && { type: type }),
		'aria-disabled': disabled,
		'tabIndex': disabled ? -1 : 0,
		'data-variant': variant || 'primary',
		...((disabled || loading) && { disabled: true }),
		...(ariaLabel && { 'aria-label': ariaLabel }),
		...(hasValidIcon && { 'data-icon-alignment': iconPosition }),
		...(variant === 'tertiary' && { 'data-theme': theme || 'light' }),
		...(underline && { 'data-underline': true }),
		...(variantSmallScreen && { 'data-variant-small-screen': variantSmallScreen })
	};

	const Component = as || 'button';

	// Keyboard handler for non-button, non-link elements
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			(e.currentTarget as HTMLElement).click();
		}
	};

	const isNonNativeButton = as !== 'button' && as !== 'a' && rest.role === 'button' && !disabled;

	return (
		<Component
			className={buttonClasses}
			{...safeRest}
			{...buttonAttributes}
			ref={ref}
			{...(isNonNativeButton && { onKeyDown: handleKeyDown })}
		>
			<>
				<span className={clsx('[ button__content ] [ text-button-base', { 'd-flex items-center': !inline }, ']')}>
					{loading ? <Loader size="xs" theme={variant === 'tertiary' ? theme : 'dark'} className="mx-sm" /> : children}
				</span>
				{hasValidIcon && !loading && (
					<svg
						className="[ button__icon icon ] [ items-center justify-center ]"
						width={iconSize}
						height={iconSize}
						fill="currentColor"
					>
						<use xlinkHref={`${BASE_PATH}/speedboat/icons/toyota-icons.svg#${icon}`} />
					</svg>
				)}
			</>
		</Component>
	);
};

export const Button = fixedForwardRef(ButtonBase);
